import dayjs from "dayjs"
import { BigNumber } from "ethers"

import { getTimestampFromIetf } from "common/helpers/date"
import { bigNumberToNumber } from "common/helpers/bignumber"
import type { DateRange } from "common/types/date"
import type { Account, VotingPowerChange } from "query/graphql"

type Addresses = {
  [address: string]: number
}

type D = Addresses & {
  timestamp: number
}

type Voters = {
  account: Pick<Account, "address">
  voteChanges: Pick<VotingPowerChange, "newBalance" | "timestamp">[]
}[]

type Props = {
  voters: Voters
  dateRange?: DateRange
  decimals: number
}

type Values = {
  dataset: D[]
}

export const useTopAdvocatesChart = ({ voters, decimals }: Props): Values => {
  const generateGovernanceChartData = (voters: Voters): D[] => {
    const items = voters.reduce((res, voter) => {
      voter.voteChanges.forEach((voteChange) => {
        const bigWeight = BigNumber.from(voteChange.newBalance)
        const nextValue = bigNumberToNumber(bigWeight, decimals)

        const day = dayjs(voteChange.timestamp)
          .set("hour", 12)
          .set("minute", 0)
          .set("second", 0)
          .toString()

        if (!res[day]) {
          res[day] = {
            timestamp: getTimestampFromIetf(day),
          }
        }

        res[day][voter.account.address] = nextValue
      })

      return res
    }, {} as { [key: string]: D })

    return Object.values(items)
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((item, i, items) => {
        if (Object.keys(item).length === voters.length + 1) {
          return item
        }

        voters.forEach((voter) => {
          const { address } = voter.account

          if (!item[address]) {
            if (!items[i - 1]?.[address]) {
              item[address] = 0
            } else {
              item[address] = items[i - 1][address]
            }
          }
        })

        return item
      })
  }

  const dataset = generateGovernanceChartData(voters)

  return { dataset }
}
